import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderBillingV2Claims: build.query<
      GetV2ProviderBillingV2ClaimsApiResponse,
      GetV2ProviderBillingV2ClaimsApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/claims`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          phase: queryArg.phase,
          patient_id: queryArg.patientId,
          provider_ids: queryArg.providerIds,
          payer_uuid: queryArg.payerUuid,
          date_from: queryArg.dateFrom,
          date_to: queryArg.dateTo,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
          include_superseded: queryArg.includeSuperseded,
        },
      }),
    }),
    getV2ProviderBillingV2Overview: build.query<
      GetV2ProviderBillingV2OverviewApiResponse,
      GetV2ProviderBillingV2OverviewApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/overview`,
        params: {
          date_from: queryArg.dateFrom,
          date_to: queryArg.dateTo,
        },
      }),
    }),
    getV2ProviderBillingV2PatientBillingQueue: build.query<
      GetV2ProviderBillingV2PatientBillingQueueApiResponse,
      GetV2ProviderBillingV2PatientBillingQueueApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/patient-billing/queue`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          patient_id: queryArg.patientId,
        },
      }),
    }),
    getV2ProviderBillingV2PatientBillingSent: build.query<
      GetV2ProviderBillingV2PatientBillingSentApiResponse,
      GetV2ProviderBillingV2PatientBillingSentApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/patient-billing/sent`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          status: queryArg.status,
          patient_id: queryArg.patientId,
        },
      }),
    }),
    getV2ProviderBillingV2PatientsByPatientIdFinancialSummary: build.query<
      GetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryApiResponse,
      GetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/patients/${queryArg.patientId}/financial-summary`,
      }),
    }),
    getV2ProviderBillingV2Remittances: build.query<
      GetV2ProviderBillingV2RemittancesApiResponse,
      GetV2ProviderBillingV2RemittancesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/remittances`,
        params: {
          page: queryArg.page,
          per_page: queryArg.perPage,
          outcome: queryArg.outcome,
          payer_name: queryArg.payerName,
          payment_method: queryArg.paymentMethod,
          date_from: queryArg.dateFrom,
          date_to: queryArg.dateTo,
          sort_by: queryArg.sortBy,
          sort_order: queryArg.sortOrder,
        },
      }),
    }),
    getV2ProviderBillingV2Work: build.query<
      GetV2ProviderBillingV2WorkApiResponse,
      GetV2ProviderBillingV2WorkApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/billing/v2/work`,
        params: {
          segment: queryArg.segment,
        },
      }),
    }),
    postV2ProviderChargeLinesPreview: build.mutation<
      PostV2ProviderChargeLinesPreviewApiResponse,
      PostV2ProviderChargeLinesPreviewApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/charge-lines/preview`,
        method: "POST",
        body: queryArg.chargeLinePreviewRequest,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderBillingV2ClaimsApiResponse =
  /** status 200 OK */ ClaimsV2Response;
export type GetV2ProviderBillingV2ClaimsApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page (max 1000) */
  perPage?: number;
  /** Filter by phase: drafts, ready, inFlight, paid, review */
  phase?: string | null;
  /** Filter by patient ID */
  patientId?: string | null;
  /** Comma-separated provider IDs */
  providerIds?: string | null;
  /** Filter by payer UUID */
  payerUuid?: string | null;
  /** Service date from (ISO 8601) */
  dateFrom?: string | null;
  /** Service date to (ISO 8601) */
  dateTo?: string | null;
  /** Sort by: created_at, service_date_from, total_charge_cents */
  sortBy?: "created_at" | "service_date_from" | "total_charge_cents";
  /** Sort order: asc or desc */
  sortOrder?: "asc" | "desc";
  /** Include claims that have been superseded by a correction or void */
  includeSuperseded?: boolean;
};
export type GetV2ProviderBillingV2OverviewApiResponse =
  /** status 200 OK */ BillingOverviewResponse;
export type GetV2ProviderBillingV2OverviewApiArg = {
  /** Filter from date (ISO 8601). Defaults to 30 days ago. */
  dateFrom?: string | null;
  /** Filter to date (ISO 8601). Defaults to now. */
  dateTo?: string | null;
};
export type GetV2ProviderBillingV2PatientBillingQueueApiResponse =
  /** status 200 OK */ PatientBillingQueueResponse;
export type GetV2ProviderBillingV2PatientBillingQueueApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page (max 1000) */
  perPage?: number;
  /** Filter by patient ID */
  patientId?: string | null;
};
export type GetV2ProviderBillingV2PatientBillingSentApiResponse =
  /** status 200 OK */ SentInvoicesResponse;
export type GetV2ProviderBillingV2PatientBillingSentApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page (max 1000) */
  perPage?: number;
  /** Filter by status: paid, open, void, uncollectible */
  status?: string | null;
  /** Filter by patient ID */
  patientId?: string | null;
};
export type GetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryApiResponse =
  /** status 200 OK */ PatientFinancialSummaryResponse;
export type GetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryApiArg = {
  patientId: string;
};
export type GetV2ProviderBillingV2RemittancesApiResponse =
  /** status 200 OK */ RemittancesV2Response;
export type GetV2ProviderBillingV2RemittancesApiArg = {
  /** Page number (1-based) */
  page?: number;
  /** Items per page (max 1000) */
  perPage?: number;
  /** Filter by outcome: all, denials, reversals, clean, imported */
  outcome?: string | null;
  /** Filter by payer name (partial match) */
  payerName?: string | null;
  /** Filter by payment method: ACH or Check */
  paymentMethod?: string | null;
  /** Payment date from (YYYY-MM-DD) */
  dateFrom?: string | null;
  /** Payment date to (YYYY-MM-DD) */
  dateTo?: string | null;
  /** Sort by column */
  sortBy?: "payment_date" | "total_payment_amount_cents" | "created_at";
  /** Sort order: asc or desc */
  sortOrder?: "asc" | "desc";
};
export type GetV2ProviderBillingV2WorkApiResponse =
  /** status 200 OK */ WorkQueueResponse;
export type GetV2ProviderBillingV2WorkApiArg = {
  /** Filter by segment: all, denied, errors, ready, toSend */
  segment?: string | null;
};
export type PostV2ProviderChargeLinesPreviewApiResponse =
  /** status 200 OK */ ChargeLinePreviewResponse;
export type PostV2ProviderChargeLinesPreviewApiArg = {
  chargeLinePreviewRequest: ChargeLinePreviewRequest;
};
export type AttentionDisposition = {
  dismissed_at?: string | null;
  dismissed_by?: string | null;
  snoozed_until?: string | null;
};
export type AttentionLane =
  | "payer_rework"
  | "patient_billing"
  | "reversal"
  | "manual_review";
export type EraTriage = {
  attention_lane?: AttentionLane | null;
  needs_attention: boolean;
  signals?: string[];
  summary: string;
};
export type WhatToDo = {
  /** True if requires user action */
  is_actionable: boolean;
  /** Severity: error, warning, success, info */
  severity: string;
  /** Untruncated action description */
  text: string;
};
export type ClaimV2Item = {
  /** User dismiss/snooze state */
  attention_disposition?: AttentionDisposition | null;
  /** Balance due in cents */
  balance_due_cents?: number | null;
  /** Claim ID */
  claim_id: string;
  /** Claim type: original, corrected, or void */
  claim_type?: string | null;
  /** Linked encounter ID */
  encounter_id?: string | null;
  /** ERA triage decision from 835 processing */
  era_triage?: EraTriage | null;
  /** Scrubbing/validation error count */
  error_count?: number;
  /** Parent claim ID — set when this is a correction or void */
  parent_claim_id?: string | null;
  /** Patient ID */
  patient_id?: string | null;
  /** Patient display name */
  patient_name?: string | null;
  /** Payer UUID */
  payer_id?: string | null;
  /** Insurance payer name */
  payer_name?: string | null;
  /** UI phase: drafts, ready, inFlight, paid, review */
  phase: string;
  /** Display label for phase */
  phase_label: string;
  /** Provider ID */
  provider_id?: string | null;
  /** Provider display name */
  provider_name?: string | null;
  /** Original 14-value status for drill-down */
  raw_status: string;
  /** Service date (ISO 8601) */
  service_date?: string | null;
  /** ID of the correction/void claim that supersedes this one */
  superseded_by_claim_id?: string | null;
  /** Total charge in cents */
  total_charge_cents?: number | null;
  /** Warning count */
  warning_count?: number;
  /** Actionable instruction */
  what_to_do: WhatToDo;
};
export type SegmentCount = {
  /** Number of items in this segment */
  count: number;
  /** Segment key */
  key: string;
  /** Display label */
  label: string;
  /** Color hint: error, warning, success, or null */
  severity?: string | null;
};
export type ClaimsV2Response = {
  /** Claim rows */
  claims: ClaimV2Item[];
  /** Current page */
  page: number;
  /** Items per page */
  per_page: number;
  /** Phase-grouped segment counts */
  segments: SegmentCount[];
  /** Human-readable summary line */
  summary: string;
  /** Total matching claims */
  total: number;
  /** Total pages */
  total_pages: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ActivityFeedEvent = {
  /** Entity ID for linking */
  link_id?: string | null;
  /** Entity type: claim, remittance, patient */
  link_type?: string | null;
  /** Patient ID for navigation */
  patient_id?: string | null;
  /** Patient display name */
  patient_name?: string | null;
  /** 'error' for denials/failures, null otherwise */
  severity?: string | null;
  /** Human-readable event description */
  text: string;
  /** ISO 8601 timestamp */
  timestamp: string;
  /** Event type: era_received, claim_created, claim_denied, payment_received, etc. */
  type: string;
};
export type ActivityFeedGroup = {
  events?: ActivityFeedEvent[];
  /** Date group: 'Today', 'Yesterday', or date string */
  group: string;
};
export type FlowHealthRow = {
  /** Total count */
  denominator?: number;
  /** Funnel label: 'Encounters → Claims' */
  label: string;
  /** Display metric: '85%' or '$7,800' */
  metric?: string;
  /** Converted count */
  numerator?: number;
};
export type SummaryStrip = {
  /** Collections last month */
  collected_last_month_cents?: number;
  /** Collections this month */
  collected_this_month_cents?: number;
  /** Trend vs last month (e.g. 12.0 for +12%) */
  collected_trend_pct?: number | null;
  /** Claims in flight */
  in_flight_count?: number;
  /** Breakdown: '5 denied, 3 errors, 6 ready to send' */
  needs_action_breakdown?: string | null;
  /** Items needing attention */
  needs_action_count?: number;
  /** Total patient accounts receivable */
  patient_ar_cents?: number;
  /** Patients with balance > 0 */
  patient_ar_count?: number;
  /** Age of oldest outstanding balance */
  patient_ar_oldest_days?: number;
};
export type BillingOverviewResponse = {
  /** Recent billing events grouped by date */
  activity_feed?: ActivityFeedGroup[];
  /** Top 5 work items (from work queue service) */
  attention_items?: {
    [key: string]: any;
  }[];
  /** Conversion funnels */
  flow_health?: FlowHealthRow[];
  /** Top-level metrics */
  summary: SummaryStrip;
};
export type PatientQueueItem = {
  /** Current patient balance in cents */
  balance_cents: number;
  /** Number of eligible charge lines */
  charge_line_count: number;
  /** Eligible charge line IDs */
  charge_line_ids?: string[];
  /** Service date range (e.g. 'Feb 20 – Mar 10') */
  date_range?: string | null;
  /** Number of encounters with charges */
  encounter_count: number;
  /** When the latest invoice was sent OR last reminded (ISO 8601). Drives the suppression window and the reminder-state badge. */
  latest_action_at?: string | null;
  /** ID of the most recent non-void invoice for this patient. Pass to POST /v2/provider/invoices/{id}/remind to send a reminder. */
  latest_invoice_id?: string | null;
  /** Patient billing account ID */
  patient_billing_account_id: string;
  /** Patient ID */
  patient_id: string;
  /** Patient display name */
  patient_name?: string | null;
  /** When queued (ISO 8601) */
  queued_at?: string | null;
  /** Number of reminders already sent for the latest invoice. */
  reminder_count?: number;
  /** Statement run ID */
  statement_run_id: string;
  /** Plain-English explanation of why patient owes */
  why: string;
};
export type PatientBillingQueueSummary = {
  /** Number of patients in queue */
  count: number;
  /** Age of oldest queued item in days */
  oldest_days?: number;
  /** Total balance across all patients */
  total_cents: number;
};
export type PatientBillingQueueResponse = {
  /** Queue items */
  items: PatientQueueItem[];
  page: number;
  per_page: number;
  /** Queue summary stats */
  summary: PatientBillingQueueSummary;
  total: number;
  total_pages: number;
};
export type SentInvoiceItem = {
  /** Invoice amount in cents */
  amount_cents: number;
  /** When invoice was created (ISO 8601) */
  created_at: string;
  /** How the invoice was delivered */
  delivery_method?: string;
  /** Linked encounter ID */
  encounter_id?: string | null;
  /** Internal invoice ID */
  invoice_id: string;
  /** Human-readable outcome: 'Paid Mar 25' or 'Open — awaiting payment' */
  outcome: string;
  /** When paid (ISO 8601) */
  paid_at?: string | null;
  /** Patient ID */
  patient_id: string;
  /** Patient display name */
  patient_name?: string | null;
  /** Whether patient received notification */
  patient_notified?: boolean;
  /** Stripe payment link URL */
  payment_url?: string | null;
  /** Who triggered the invoice */
  sent_by?: string | null;
  /** Invoice status: paid, open, void, etc. */
  status: string;
  /** Stripe invoice ID */
  stripe_invoice_id: string;
};
export type SentInvoicesSummary = {
  /** Total sent invoices */
  count: number;
  /** Number of failed/void invoices */
  failed_count?: number;
  /** Number of open invoices */
  open_count?: number;
  /** Number of paid invoices */
  paid_count?: number;
  /** Total paid amount */
  paid_total_cents?: number;
  /** Total invoiced amount */
  total_cents: number;
};
export type SentInvoicesResponse = {
  /** Invoice items */
  items: SentInvoiceItem[];
  page: number;
  per_page: number;
  /** Sent summary stats */
  summary: SentInvoicesSummary;
  total: number;
  total_pages: number;
};
export type LedgerEntrySourceRef = {
  id?: string | null;
  type: string;
};
export type LedgerEntryItem = {
  actor_user_id?: string | null;
  amount_cents: number;
  created_at: string;
  description?: string | null;
  entry_id: string;
  entry_type: string;
  entry_type_label: string;
  party_type: string;
  source_ref?: LedgerEntrySourceRef | null;
};
export type PatientBillingStatus = {
  /** How the last invoice was delivered */
  last_invoice_delivery_method?: string | null;
  /** When the last invoice was sent (ISO 8601) */
  last_invoice_sent_at?: string | null;
  /** When the ERA queue item was created (ISO 8601) */
  queued_at?: string | null;
  /** Billing status code */
  status:
    | "in_queue"
    | "open_invoice"
    | "balance_due"
    | "paid"
    | "credit"
    | "no_activity";
  /** Human-readable label, rendered as-is */
  status_label: string;
};
export type EncounterAdjustmentsSplit = {
  insurance_adjustment_cents?: number;
  other_cents?: number;
  write_off_cents?: number;
};
export type ChargeLineItem = {
  adjusted_cents?: number;
  allowed_cents?: number | null;
  charge_line_id: string;
  charged_cents?: number;
  cpt_code?: string | null;
  description?: string | null;
  insurance_paid_cents?: number;
  patient_paid_cents?: number;
  patient_responsibility_cents?: number | null;
  status?: string;
  units?: number;
};
export type EncounterClaimSummary = {
  claim_id: string;
  paid_at?: string | null;
  status: string;
  status_label: string;
  submitted_at?: string | null;
  total_charge_cents?: number;
};
export type PatientResponsibilityBreakdown = {
  coinsurance_cents?: number;
  copay_cents?: number;
  deductible_cents?: number;
  other_cents?: number;
};
export type EncounterLedgerGroup = {
  /** Adjustments (kept for backwards compat) */
  adjusted_cents?: number;
  /** Adjustments split by type */
  adjustments?: EncounterAdjustmentsSplit | null;
  /** CPT-level charge line breakdown */
  charge_lines?: ChargeLineItem[];
  /** Total charges for this encounter */
  charged_cents?: number;
  /** Most recent claim for this encounter, null if no claim filed */
  claim?: EncounterClaimSummary | null;
  /** Encounter ID */
  encounter_id?: string | null;
  /** Encounter type + primary CPT, e.g. 'Follow-up — 90834' */
  encounter_type_label?: string | null;
  /** Ledger entries for this encounter */
  entries?: LedgerEntryItem[];
  /** Insurance paid */
  insurance_paid_cents?: number;
  /** Invoice IDs from the top-level invoices[] that belong to this encounter */
  linked_invoice_ids?: string[];
  /** Remaining balance */
  patient_balance_cents?: number;
  /** Patient responsibility */
  patient_due_cents?: number;
  /** Patient paid */
  patient_paid_cents?: number;
  /** Copay/coinsurance/deductible split, null pre-adjudication */
  patient_responsibility_breakdown?: PatientResponsibilityBreakdown | null;
  /** Primary insurance payer name, null if self-pay */
  primary_payer_name?: string | null;
  /** Provider user ID */
  provider_id?: string | null;
  /** Provider display name */
  provider_name?: string | null;
  /** Service date */
  service_date?: string | null;
};
export type PatientInvoiceHistoryItem = {
  amount_cents: number;
  created_at: string;
  delivery_method?: string;
  encounter_id?: string | null;
  invoice_id: string;
  paid_at?: string | null;
  payment_url?: string | null;
  status: string;
};
export type PatientFinancialSummaryTotals = {
  /** Credit balance (negative patient balance = credit) */
  credit_balance_cents?: number;
  /** Payer-party balance */
  insurance_owed_cents?: number;
  /** Most recent payment date */
  last_payment_date?: string | null;
  /** Patient-party balance */
  patient_owed_cents?: number;
  /** Sum of adjustments */
  total_adjustments_cents?: number;
  /** Current patient balance (positive = owes) */
  total_balance_cents?: number;
  /** Sum of all charges */
  total_charges_cents?: number;
  /** Sum of payer payments */
  total_insurance_paid_cents?: number;
  /** Sum of patient payments */
  total_patient_paid_cents?: number;
};
export type PatientFinancialSummaryResponse = {
  /** Ledger entries not linked to a specific encounter (e.g. account-level write-offs, standalone charges). Excluded from encounters[] — render in the full transaction ledger. */
  account_level_entries?: LedgerEntryItem[];
  /** Computed billing status for the PatientResponsibilityPanel */
  billing_status: PatientBillingStatus;
  /** Encounter-grouped ledger entries */
  encounters?: EncounterLedgerGroup[];
  /** Invoice history */
  invoices?: PatientInvoiceHistoryItem[];
  patient_billing_account_id?: string | null;
  patient_id: string;
  patient_name?: string | null;
  summary: PatientFinancialSummaryTotals;
};
export type RemittanceV2Item = {
  /** Number of claims in this ERA */
  claim_count?: number;
  /** Created timestamp */
  created_at?: string | null;
  /** Number of denied claims */
  denial_count?: number;
  /** Outcome classification: clean, denials, reversals */
  outcome: string;
  /** Human-readable outcome: 'Clean', '8 denied', '1 reversed' */
  outcome_label: string;
  /** Patient name if single-patient remittance */
  patient_name?: string | null;
  /** Total patient responsibility in cents */
  patient_responsibility_cents?: number;
  /** Payer identifier */
  payer_id?: string | null;
  /** Payer name */
  payer_name?: string | null;
  /** Payment date (ISO 8601) */
  payment_date?: string | null;
  /** Payment method: ACH or Check */
  payment_method?: string | null;
  /** Remittance ID */
  remittance_id: string;
  /** Number of reversed claims */
  reversal_count?: number;
  /** Source system */
  source_system?: string | null;
  /** Total payment in cents */
  total_paid_cents?: number | null;
  /** Check or EFT trace number */
  trace_number: string;
};
export type SegmentCount2 = {
  /** Number of items in this segment */
  count: number;
  /** Segment key */
  key: string;
  /** Display label */
  label: string;
  /** Color hint: error, warning, success, or null */
  severity?: string | null;
};
export type RemittancesV2Response = {
  /** Current page */
  page: number;
  /** Items per page */
  per_page: number;
  /** Remittance rows */
  remittances: RemittanceV2Item[];
  /** Outcome-grouped segment counts */
  segments: SegmentCount2[];
  /** Human-readable summary line */
  summary: string;
  /** Total matching remittances */
  total: number;
  /** Total pages */
  total_pages: number;
};
export type WorkItem = {
  /** Button text */
  action_label: string;
  /** Deep link path */
  action_link?: string | null;
  /** Relative timestamp */
  age?: string | null;
  /** Category label: DENIED CLAIM, CLAIM ERRORS, etc. */
  category: string;
  /** Context line: provider · payer */
  context?: string | null;
  /** Service date */
  date?: string | null;
  /** Human-readable explanation (denial code, top blocker) */
  explanation?: string | null;
  /** Work item ID (claim_id, statement_run_id, etc.) */
  id: string;
  /** Meta line: CPT · amounts · error counts */
  meta?: string | null;
  /** Patient ID */
  patient_id?: string | null;
  /** Patient display name */
  patient_name?: string | null;
  /** Priority group: now, soon, ready */
  priority: string;
  /** What to do about this item */
  recommended_action?: string | null;
  /** Segment key: denied, errors, ready, toSend */
  segment: string;
  /** Severity: error, warning, success */
  severity: string;
  /** Source entity ID */
  source_id: string;
  /** Source: claim, encounter, statement_run */
  source_type: string;
};
export type WorkGroup = {
  /** Number of items in this group */
  count?: number;
  /** Work items */
  items?: WorkItem[];
  /** Priority key: now, soon, ready */
  key: string;
  /** Group label */
  label: string;
  /** Group description */
  sublabel: string;
};
export type SegmentCount3 = {
  /** Number of items in this segment */
  count: number;
  /** Segment key */
  key: string;
  /** Display label */
  label: string;
  /** Color hint: error, warning, success, or null */
  severity?: string | null;
};
export type WorkQueueResponse = {
  /** Priority-grouped work items */
  groups: WorkGroup[];
  /** Segment counts for filter pills */
  segments: SegmentCount3[];
  /** Human-readable summary: '14 items need attention' */
  summary: string;
};
export type ChargeLinePreviewLine = {
  /** True if this line is already INVOICED or PAID — the modal should warn the user before re-billing. */
  already_invoiced?: boolean;
  /** Sum of payments allocated to this charge_line via payment_allocations. Useful for showing 'paid X of Y' on the receipt. Account-level / unallocated payments don't show here — see ChargeLinePreviewResponse.unallocated_credit_cents. */
  applied_payment_cents?: number;
  charge_line_id: string;
  /** CPT or HCPCS billing code, when known */
  cpt_code?: string | null;
  description?: string | null;
  /** Remaining amount the patient owes for this line, in cents. Already net of any payment_allocations targeting this line — so for a $200 charge with $200 allocated against it, this is 0. */
  patient_due_cents: number;
};
export type ChargeLinePreviewEncounter = {
  /** Sum of patient_due_cents across this encounter's lines */
  encounter_due_cents: number;
  /** Null for ad-hoc charges with no encounter linkage */
  encounter_id?: string | null;
  /** Display label, e.g. 'Follow-up' */
  encounter_type_label?: string | null;
  lines?: ChargeLinePreviewLine[];
  provider_name?: string | null;
  /** Canonical billing date as ISO 8601 (date) */
  service_date?: string | null;
};
export type ChargeLinePreviewResponse = {
  encounters?: ChargeLinePreviewEncounter[];
  /** Patient owning all the requested charge_lines (validated server-side; mixed patients return 400). */
  patient_id: string;
  /** BE-synthesized default for the modal's 'reason' field. FE should pre-fill this; user can edit before sending. */
  suggested_reason: string;
  /** Authoritative amount to bill — equals the patient's ledger balance, clamped to >= 0. Includes all payments and write-offs. May be lower than the sum of per-line patient_due_cents when there are unallocated credits (see unallocated_credit_cents). */
  total_due_cents: number;
  /** Difference between sum(per-line remaining) and the ledger total. Represents account-level credits (manual cash, write-offs, prepays) that lowered the ledger but didn't target any specific charge_line. The FE should render this as a separate 'Applied credits' line so the receipt totals reconcile to total_due_cents. */
  unallocated_credit_cents?: number;
};
export type ServerErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the server error */
  status?: number;
};
export type ChargeLinePreviewRequest = {
  /** Explicit list of charge_line_ids to preview. All must belong to the same patient. Use this for partial settlement / statement-run flows where the caller already has IDs. */
  charge_line_ids?: string[] | null;
  /** Resolve all charge_lines on this encounter. Ergonomic shorthand for the common case (ledger tab, settle modal). Mutually exclusive with charge_line_ids. */
  encounter_id?: string | null;
};
export const {
  useGetV2ProviderBillingV2ClaimsQuery,
  useGetV2ProviderBillingV2OverviewQuery,
  useGetV2ProviderBillingV2PatientBillingQueueQuery,
  useGetV2ProviderBillingV2PatientBillingSentQuery,
  useGetV2ProviderBillingV2PatientsByPatientIdFinancialSummaryQuery,
  useGetV2ProviderBillingV2RemittancesQuery,
  useGetV2ProviderBillingV2WorkQuery,
  usePostV2ProviderChargeLinesPreviewMutation,
} = injectedRtkApi;
